import type { UpsertContactAddressDto } from '@/hooks/contacts/useContactAddresses';
import type { ContactAddress } from '@/types/contact';

export type NanpCountry = 'US' | 'CA';

export const NANP_COUNTRIES: { code: NanpCountry; label: string; flag: string }[] = [
  { code: 'US', label: 'United States', flag: '🇺🇸' },
  { code: 'CA', label: 'Canada', flag: '🇨🇦' },
];

/** Normalize a stored country (code or name) to a NANP code; defaults to US. */
export function nanpCountry(value: string | null | undefined): NanpCountry {
  const normalized = (value ?? '').trim().toUpperCase();
  return normalized === 'CA' || normalized === 'CANADA' ? 'CA' : 'US';
}

export function nanpCountryMeta(code: NanpCountry) {
  return NANP_COUNTRIES.find((entry) => entry.code === code) ?? NANP_COUNTRIES[0]!;
}

/**
 * Single normalized shape for a contact address across both surfaces (the
 * contact-create panel and the standalone create/edit sheet). The UI works only
 * with this draft; adapters at the edges map it to each surface's DTO so the two
 * never diverge again. `customFields`/`activeCustomFieldKeys` are draft buffers
 * for the create flows (persisted to the address custom-field endpoint after the
 * address has an id); the live edit surface uses {@link CustomFieldsSection}.
 */
export interface AddressDraft {
  street1: string;
  street2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  isService: boolean;
  isBilling: boolean;
  isPrimary: boolean;
  title: string;
  label: string;
  isTaxExempt: boolean;
  taxExemptCertificate: string;
  taxExemptExpiry: string;
  accessNotes: string;
  customFields: Record<string, string>;
  activeCustomFieldKeys: string[];
}

export const EMPTY_ADDRESS_DRAFT: AddressDraft = {
  street1: '',
  street2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'US',
  isService: true,
  isBilling: false,
  isPrimary: true,
  title: '',
  label: '',
  isTaxExempt: false,
  taxExemptCertificate: '',
  taxExemptExpiry: '',
  accessNotes: '',
  customFields: {},
  activeCustomFieldKeys: [],
};

export function draftFromContactAddress(address: ContactAddress): AddressDraft {
  return {
    street1: address.street1 ?? address.street ?? '',
    street2: address.street2 ?? '',
    city: address.city ?? '',
    state: address.state ?? '',
    postalCode: address.postalCode ?? address.zip ?? '',
    country: nanpCountry(address.country),
    isService: Boolean(address.isService),
    isBilling: Boolean(address.isBilling),
    isPrimary: Boolean(address.isPrimary),
    title: address.title ?? '',
    label: address.label ?? '',
    isTaxExempt: Boolean(address.isTaxExempt),
    taxExemptCertificate: address.taxExemptCertificate ?? '',
    taxExemptExpiry: address.taxExemptExpiry ? address.taxExemptExpiry.slice(0, 10) : '',
    accessNotes: address.accessNotes ?? '',
    customFields: {},
    activeCustomFieldKeys: [],
  };
}

/** Has the user entered anything worth validating/saving for this address? */
export function isAddressDraftStarted(draft: AddressDraft): boolean {
  return Boolean(
    draft.street1.trim() ||
    draft.street2.trim() ||
    draft.city.trim() ||
    draft.state.trim() ||
    draft.postalCode.trim() ||
    draft.title.trim() ||
    draft.label.trim() ||
    draft.accessNotes.trim(),
  );
}

export function isAddressDraftLocationComplete(draft: AddressDraft): boolean {
  return Boolean(
    draft.street1.trim() && draft.city.trim() && draft.state.trim() && draft.postalCode.trim(),
  );
}

export function isAddressTaxExemptExpired(draft: AddressDraft): boolean {
  if (!draft.isTaxExempt || !draft.taxExemptExpiry) return false;
  const expiry = new Date(draft.taxExemptExpiry);
  return !Number.isNaN(expiry.getTime()) && expiry.getTime() < Date.now();
}

/**
 * Lightweight embedded address for `POST /contacts` (its `ContactAddressDto`
 * uses `street`/`zip` and does not accept title/label/tax — those are PATCHed
 * onto the created address afterwards via {@link draftSpecExtras}).
 */
export function draftToEmbeddedAddress(draft: AddressDraft): ContactAddress {
  return {
    isService: draft.isService,
    isBilling: draft.isBilling,
    isPrimary: draft.isService ? draft.isPrimary : false,
    street: draft.street1.trim(),
    street2: draft.street2.trim() || undefined,
    city: draft.city.trim(),
    state: draft.state.trim(),
    zip: draft.postalCode.trim(),
    country: nanpCountry(draft.country),
    accessNotes: draft.accessNotes.trim() || undefined,
  };
}

/** Full upsert DTO for the standalone `contacts/:id/addresses` endpoints. */
export function draftToUpsertDto(draft: AddressDraft): UpsertContactAddressDto {
  return {
    title: draft.title.trim() || null,
    label: draft.label.trim() || null,
    isBilling: draft.isBilling,
    isService: draft.isService,
    street1: draft.street1.trim(),
    street2: draft.street2.trim() || null,
    city: draft.city.trim(),
    state: draft.state.trim(),
    postalCode: draft.postalCode.trim(),
    country: nanpCountry(draft.country),
    isPrimary: draft.isService ? draft.isPrimary : false,
    isTaxExempt: draft.isTaxExempt,
    taxExemptCertificate: draft.taxExemptCertificate.trim() || null,
    taxExemptExpiry: draft.taxExemptExpiry || null,
    accessNotes: draft.accessNotes.trim() || null,
  };
}

/**
 * Spec-only fields (title/label/tax) not carried by the embedded create DTO.
 * Returned as a partial upsert payload to PATCH onto the just-created address;
 * `null` when there is nothing extra to persist.
 */
export function draftSpecExtras(draft: AddressDraft): Partial<UpsertContactAddressDto> | null {
  const extras: Partial<UpsertContactAddressDto> = {};
  if (draft.title.trim()) extras.title = draft.title.trim();
  if (draft.label.trim()) extras.label = draft.label.trim();
  if (draft.isTaxExempt) {
    extras.isTaxExempt = true;
    if (draft.taxExemptCertificate.trim())
      extras.taxExemptCertificate = draft.taxExemptCertificate.trim();
    if (draft.taxExemptExpiry) extras.taxExemptExpiry = draft.taxExemptExpiry;
  }
  return Object.keys(extras).length > 0 ? extras : null;
}

/** "Apt 4 · Vancouver, BC V6Z 1Y4 · 🇨🇦 Canada" for the resolved address card. */
export function addressDraftSubline(draft: AddressDraft): string {
  const cityState = [draft.city.trim(), draft.state.trim()].filter(Boolean).join(', ');
  const place = [cityState, draft.postalCode.trim()].filter(Boolean).join(' ');
  const meta = nanpCountryMeta(nanpCountry(draft.country));
  return [draft.street2.trim(), place, `${meta.flag} ${meta.label}`].filter(Boolean).join(' · ');
}
